import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import PropTypes from 'prop-types';

import { getLabel } from '../../../../labels/labelling-service';
import { useOutsideClick } from '../../../hooks/use-outside-click';
import { IconArrowDown, IconClose } from '../../1-foundation/icons/icons';
import Option from '../../2-atoms/option/option';
import Cta from '../../2-atoms/cta/cta';
import SectionTitle from '../../2-atoms/section-title/section-title';
import { modifierList as sectionTitleModifierList } from '../../2-atoms/section-title/props-list';
import { modifierList as iconsModifiersList } from '../../1-foundation/icons/props-list';

const renderOption = ({ option, selectedOptions, maxOptions, onClick }) => {
  const isActive = selectedOptions.map((selectedOption) => selectedOption.id).includes(option.id);
  const isDisabled = !isActive && maxOptions > 1 && selectedOptions.length >= maxOptions;
  const optionShape = maxOptions > 1 ? 'checkbox' : 'radio';
  return (
    <Option
      key={option.id}
      isActive={isActive}
      onClick={() => onClick(option)}
      isDisabled={isDisabled}
      modifiers={[optionShape]}
      text={option.text}
    />
  );
};

const getDropdownTitle = ({ selectedOptions, title, editTitle, maxOptions }) => {
  if (maxOptions === 1) {
    return selectedOptions.length ? selectedOptions[0].text : title;
  }
  return selectedOptions.length ? editTitle : title;
};

/**
 *
 * @param {object} options example: {id: 1, label: 'Cagliari', enabled: true}
 * @param {object} selectedOptions example: {id: 1, label: 'Cagliari', enabled: true}
 * @returns
 */
const DropdownBasic = ({
  isOpen = false,
  maxOptions = 1,
  onHeaderClick,
  onCancel,
  onConfirm,
  onOptionClick,
  options,
  selectedOptions = [],
  title,
  editTitle,
  messageSelect,
  sectionTitle,
  sectionTitleModifiers,
  iconsColor,
}) => {
  const initialTitle = getDropdownTitle({ selectedOptions, title, editTitle, maxOptions });
  const [dropdownTitle, setDropdownTitle] = useState(initialTitle);
  const dropdownRef = useRef();
  const message = messageSelect[selectedOptions.length]?.replace('%s', title);

  useOutsideClick(dropdownRef, onCancel);

  useEffect(() => {
    const updatedTitle = getDropdownTitle({ selectedOptions, title, editTitle, maxOptions });
    setDropdownTitle(updatedTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, title]);

  const handleConfirm = () => {
    onConfirm();
  };

  let messageClassName = ['c-dropdown__body-message'];
  if (maxOptions === 1) {
    messageClassName = [...messageClassName, 'c-dropdown__body-message--hide-desktop'];
  }

  return (
    <div className="c-dropdown c-dropdown--basic" data-state={isOpen && 'open'} ref={dropdownRef}>
      <div className="c-dropdown__section-title">
        <SectionTitle modifiers={sectionTitleModifiers}>{sectionTitle}</SectionTitle>
        <button className="c-dropdown__section-title-close" type="button" onClick={onCancel}>
          <IconClose modifiers={[iconsColor, 'navigation', 'close']} />
        </button>
      </div>
      <div className="c-dropdown__header" onClick={onHeaderClick}>
        <div className="c-dropdown__header-text">{dropdownTitle}</div>
        <button className="c-dropdown__header-button" type="button">
          <IconArrowDown
            modifiers={['arrow-navigation', 'arrow-down', 'small', 'c-dark']}
            classNames="c-dropdown__arrow-icon"
          />
        </button>
      </div>
      <div className="c-dropdown__body">
        <div className={messageClassName.join(' ')} dangerouslySetInnerHTML={{ __html: message }} />
        {options.map((option) => renderOption({ option, selectedOptions, maxOptions, onClick: onOptionClick }))}
      </div>
      <div className="c-dropdown__footer">
        <Cta modifiers={['no-outline']} onClick={onCancel} disabledStyle>
          {getLabel('global.cancel')}
        </Cta>
        <Cta modifiers={['no-outline']} onClick={handleConfirm}>
          {getLabel('global.confirm')}
        </Cta>
      </div>
    </div>
  );
};
DropdownBasic.propTypes = {
  isOpen: PropTypes.bool,
  maxOptions: PropTypes.number,
  messageSelect: PropTypes.array,
  onHeaderClick: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  onOptionClick: PropTypes.func,
  options: PropTypes.array,
  selectedOptions: PropTypes.array,
  title: PropTypes.string,
  editTitle: PropTypes.string,
  sectionTitle: PropTypes.string,
  sectionTitleModifiers: PropTypes.arrayOf(PropTypes.oneOf(sectionTitleModifierList)),
  iconsColor: PropTypes.oneOf(iconsModifiersList).isRequired,
};

export default DropdownBasic;
